import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Pencil, Save, X } from 'lucide-react'
import { areasApi } from '../../lib/api'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import type { SolutionArea } from '../../types'

export function AreasEditor() {
  const { t } = useTranslation()
  const [areas, setAreas] = useState<SolutionArea[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  const schema = z.object({
    title: z.string().min(2, t('admin.areas.errors.title')),
    description: z.string().min(5, t('admin.areas.errors.description')),
    iconName: z.string().min(1, t('admin.areas.errors.iconName')),
    displayOrder: z.preprocess((val) => Number(val), z.number().min(1)),
    isActive: z.boolean(),
  })

  type FormData = z.infer<typeof schema>

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema) as Resolver<FormData>,
  })

  const loadAreas = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await areasApi.getAll()
      setAreas(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.areas.error'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    let cancelled = false
    areasApi
      .getAll()
      .then((data) => {
        if (!cancelled) setAreas(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t('admin.areas.error'))
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [t])

  const handleEdit = (area: SolutionArea) => {
    setEditingId(area.id)
    reset({
      title: area.title,
      description: area.description,
      iconName: area.iconName,
      displayOrder: area.displayOrder,
      isActive: area.isActive,
    })
  }

  const handleCancel = () => {
    setEditingId(null)
    reset()
  }

  const onSubmit = async (data: FormData) => {
    if (!editingId) return
    try {
      await areasApi.update(editingId, data)
      await loadAreas()
      handleCancel()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.areas.error'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">{t('admin.areas.title')}</h1>
      </div>

      {error && (
        <Card variant="default" className="border-error/30">
          <p className="text-error">{error}</p>
        </Card>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {areas.map((area) => (
            <Card key={area.id} variant="default" hover={false}>
              {editingId === area.id ? (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-text-primary">
                      {t('admin.areas.form.title')}
                    </label>
                    <input
                      {...register('title')}
                      className="w-full rounded-lg border border-border-subtle bg-bg px-3 py-2 text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                    {errors.title && <p className="mt-1 text-sm text-error">{errors.title.message}</p>}
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-text-primary">
                      {t('admin.areas.form.description')}
                    </label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="w-full rounded-lg border border-border-subtle bg-bg px-3 py-2 text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent resize-none"
                    />
                    {errors.description && <p className="mt-1 text-sm text-error">{errors.description.message}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-text-primary">
                        Icono
                      </label>
                      <input
                        {...register('iconName')}
                        className="w-full rounded-lg border border-border-subtle bg-bg px-3 py-2 text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                      {errors.iconName && <p className="mt-1 text-sm text-error">{errors.iconName.message}</p>}
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-text-primary">
                        Orden
                      </label>
                      <input
                        type="number"
                        {...register('displayOrder')}
                        className="w-full rounded-lg border border-border-subtle bg-bg px-3 py-2 text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                      />
                      {errors.displayOrder && <p className="mt-1 text-sm text-error">{errors.displayOrder.message}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`isActive-${area.id}`}
                      {...register('isActive')}
                      className="h-4 w-4 rounded border-border-subtle bg-bg text-accent focus:ring-accent"
                    />
                    <label htmlFor={`isActive-${area.id}`} className="text-sm text-text-secondary">Activo</label>
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" size="sm" isLoading={isSubmitting}>
                      <Save className="mr-2 h-4 w-4" />
                      {t('admin.areas.save')}
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
                      <X className="mr-2 h-4 w-4" />
                      {t('admin.areas.cancel')}
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-text-primary">{area.title}</h3>
                      <p className="mt-1 text-sm text-text-secondary">{area.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleEdit(area)}
                      className="rounded-lg p-2 text-text-secondary transition-colors hover:bg-surface-elevated hover:text-accent-light"
                      aria-label={t('admin.areas.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-4 flex items-center gap-3 border-t border-border pt-3 text-xs text-text-muted">
                    <span>Icono: {area.iconName}</span>
                    <span>Orden: {area.displayOrder}</span>
                    <span className={area.isActive ? 'text-emerald-400' : 'text-text-muted'}>
                      {area.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
